import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, KeyRound, Copy, CheckCircle2, AlertTriangle,
  Loader2, Undo2, Trash2, Play, X, Info,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT = "#0F766E";
const MIN_KEY = 32;

function Panel({ children, tone = "plain" }) {
  const border = {
    plain: "border-slate-200",
    warn: "border-amber-300 bg-amber-50",
    good: "border-emerald-300 bg-emerald-50",
  }[tone];
  return <div className={`rounded-2xl border ${border} bg-white p-5 shadow-sm`}>{children}</div>;
}

function Step({ n, title, state, children }) {
  const ring = state === "done" ? "bg-emerald-600" : state === "active" ? "" : "bg-slate-300";
  return (
    <section className={`rounded-2xl border p-5 ${state === "active" ? "border-teal-300 bg-white shadow-md" : "border-slate-200 bg-white/60"}`}>
      <div className="flex items-start gap-3">
        <span
          className={`w-7 h-7 rounded-full text-white text-sm font-bold flex items-center justify-center shrink-0 ${ring}`}
          style={state === "active" ? { backgroundColor: ACCENT } : undefined}
        >
          {state === "done" ? <CheckCircle2 className="w-4 h-4" /> : n}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className={`font-bold ${state === "todo" ? "text-slate-400" : "text-slate-900"}`}>{title}</h2>
          <div className={`mt-2 text-sm leading-relaxed ${state === "todo" ? "text-slate-400" : "text-slate-600"}`}>
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function FileKeyRotation() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [newKey, setNewKey] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const stop = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/file-key/status`, { withCredentials: true });
      setStatus(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Stato non disponibile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { stop.current = true; }, []);

  const call = async (path, body) => {
    const { data } = await axios.post(`${API}/file-key/${path}`, body || {}, { withCredentials: true });
    return data;
  };

  const onStart = async () => {
    setBusy("start");
    try {
      const res = await call("start", { new_key: newKey });
      toast.success(`Cartella di destinazione: ${res.prefix}`);
      setNewKey("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preparazione non riuscita");
    } finally {
      setBusy("");
    }
  };

  const onCopy = async () => {
    setBusy("copy");
    stop.current = false;
    try {
      for (;;) {
        const res = await call("copy");
        setStatus((s) => (s ? { ...s, copied_files: res.copied_files, total_files: res.total_files } : s));
        if (res.failures?.length) {
          res.failures.forEach((f) => toast.error(`${f.name}: ${f.error}`));
        }
        if (res.done || stop.current) break;
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Copia interrotta");
      await load();
    } finally {
      setBusy("");
    }
  };

  const onVerify = async () => {
    setBusy("verify");
    setVerifyResult(null);
    try {
      const res = await call("verify");
      setVerifyResult(res);
      if (res.passed) toast.success(`${res.verified} file verificati`);
      else toast.error("Verifica non superata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verifica non riuscita");
    } finally {
      setBusy("");
    }
  };

  const onSwitch = async () => {
    setBusy("switch");
    try {
      const res = await call("switch");
      toast.success(`Copia attiva: ${res.switched_to}`);
      setVerifyResult(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Attivazione non riuscita");
    } finally {
      setBusy("");
      setConfirm(null);
    }
  };

  const onRollback = async () => {
    setBusy("rollback");
    try {
      await call("rollback");
      toast.success("Copia precedente riattivata");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ritorno non riuscito");
    } finally {
      setBusy("");
      setConfirm(null);
    }
  };

  const onDiscard = async () => {
    setBusy("discard");
    try {
      const res = await call("discard-previous");
      toast.success(`${res.deleted} oggetti eliminati`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    } finally {
      setBusy("");
      setConfirm(null);
    }
  };

  const onCancel = async () => {
    setBusy("cancel");
    try {
      const res = await call("cancel");
      toast.success(`${res.removed} oggetti parziali rimossi`);
      setVerifyResult(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Annullamento non riuscito");
    } finally {
      setBusy("");
      setConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <main className="max-w-3xl mx-auto px-4 py-12 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  const step = status?.step || "idle";
  const total = status?.total_files || 0;
  const copied = status?.copied_files || 0;
  const pct = total ? Math.round((copied / total) * 100) : 0;

  const stepState = (name) => {
    const order = ["idle", "copying", "ready_to_switch", "switched"];
    const here = order.indexOf(step);
    const mine = order.indexOf(name);
    if (here === mine) return "active";
    return here > mine ? "done" : "todo";
  };

  return (
    <div className="min-h-screen">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/settings" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna alle impostazioni
        </Link>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: ACCENT }}>
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Rotazione della chiave dei file</h1>
        </div>

        <div className="text-sm text-slate-600 leading-relaxed mb-6 space-y-2">
          <p>
            I file archiviati su R2 sono cifrati con una chiave dedicata. La procedura non
            sostituisce gli oggetti esistenti: crea una seconda copia dell'archivio cifrata con la
            chiave nuova, mentre Core continua a leggere gli originali. Al termine una singola
            operazione sposta la lettura sulla copia nuova. Gli originali restano disponibili
            finché non vengono eliminati alla fase 5.
          </p>
          <p>
            La chiave nuova viene registrata nelle impostazioni dell'applicazione, cifrate a riposo
            nel database, e caricata a ogni avvio. Non sono necessari interventi su Railway.
          </p>
        </div>

        <div className="space-y-4">
          <Panel tone={status?.key_is_weak ? "warn" : "plain"}>
            <div className="flex items-start gap-3">
              <KeyRound className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <h2 className="font-bold text-slate-900">Stato attuale</h2>
                <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate-500">Chiave in uso</dt>
                    <dd className="font-mono text-slate-800">{status?.active?.key}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate-500">Copia in lettura</dt>
                    <dd className="font-mono text-slate-800 truncate">{status?.active?.label}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate-500">Bucket</dt>
                    <dd className="font-mono text-slate-800 truncate">{status?.bucket}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-slate-500">File archiviati</dt>
                    <dd className="font-semibold text-slate-800 tabular-nums">{total}</dd>
                  </div>
                </dl>
                {status?.key_is_weak && (
                  <p className="mt-3 text-sm text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                      La chiave in uso è più corta di {MIN_KEY} caratteri. Viene trasformata con un
                      hash privo di rallentamento deliberato, quindi un valore corto è ricavabile
                      per tentativi da chi disponesse degli oggetti del bucket.
                    </span>
                  </p>
                )}
              </div>
            </div>
          </Panel>

          <Step n={1} title="Chiave nuova" state={stepState("idle")}>
            {step === "idle" ? (
              <>
                <p className="mb-3">
                  Almeno {MIN_KEY} caratteri casuali. La chiave non viene più mostrata dopo questa
                  fase e non è ricavabile dall'applicazione: senza di essa i file copiati non sono
                  decifrabili.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder={`Chiave nuova (minimo ${MIN_KEY} caratteri)`}
                    className="border-slate-200 font-mono text-xs"
                  />
                  <Button
                    onClick={onStart}
                    disabled={newKey.length < MIN_KEY || busy === "start"}
                    className="text-white shrink-0"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {busy === "start" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1.5" />Prepara</>}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-slate-400 tabular-nums">{newKey.length} caratteri</p>
              </>
            ) : (
              <p>
                Cartella di destinazione: <code className="px-1 bg-slate-100 rounded">{status?.pending?.prefix || status?.active?.prefix}</code>.
              </p>
            )}
          </Step>

          <Step n={2} title="Copia dell'archivio" state={stepState("copying")}>
            <p className="mb-3">
              Ogni file viene letto con la chiave in uso e riscritto nella cartella di destinazione
              con la chiave nuova. Gli oggetti originali non vengono modificati. L'operazione
              procede a lotti e può essere interrotta: i file già copiati sono riconosciuti dalla
              presenza dell'oggetto nel bucket, quindi una nuova esecuzione riprende da quelli
              mancanti.
            </p>
            {(step === "copying" || step === "ready_to_switch") && (
              <>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: ACCENT }} />
                </div>
                <p className="mt-1.5 text-xs text-slate-500 tabular-nums">{copied} di {total} file copiati</p>
              </>
            )}
            {step === "copying" && (
              <div className="flex flex-wrap gap-2 mt-3">
                <Button onClick={onCopy} disabled={busy === "copy"} className="text-white" style={{ backgroundColor: ACCENT }}>
                  {busy === "copy" ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Copia in corso</> : <><Copy className="w-4 h-4 mr-1.5" />Avvia la copia</>}
                </Button>
                {busy === "copy" && (
                  <Button variant="outline" onClick={() => { stop.current = true; }} className="border-slate-200">
                    Interrompi
                  </Button>
                )}
                {busy !== "copy" && (
                  <Button variant="outline" onClick={() => setConfirm("cancel")} className="border-slate-200 text-slate-500">
                    <X className="w-4 h-4 mr-1.5" />Annulla la rotazione
                  </Button>
                )}
              </div>
            )}
          </Step>

          <Step n={3} title="Verifica della copia" state={stepState("ready_to_switch")}>
            <p className="mb-3">
              La verifica controlla che per ogni file esista l'oggetto corrispondente nella cartella
              di destinazione e che il suo primo blocco si decifri con la chiave nuova. La sola
              presenza dell'oggetto non garantisce che sia leggibile.
            </p>
            {step === "ready_to_switch" && (
              <>
                <Button onClick={onVerify} disabled={busy === "verify"} variant="outline" className="border-slate-200">
                  {busy === "verify" ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Verifica in corso</> : <><ShieldCheck className="w-4 h-4 mr-1.5" />Esegui la verifica</>}
                </Button>
                {verifyResult && (
                  <div className={`mt-3 rounded-xl border p-3 text-sm ${verifyResult.passed ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-red-300 bg-red-50 text-red-900"}`}>
                    {verifyResult.passed ? (
                      <>File verificati: <strong className="tabular-nums">{verifyResult.verified}</strong> su {verifyResult.total_files}.</>
                    ) : (
                      <>
                        <p className="font-semibold mb-1">Verifica non superata.</p>
                        {verifyResult.missing?.length > 0 && (
                          <p>Assenti dalla copia: {verifyResult.missing.join(", ")}.</p>
                        )}
                        {verifyResult.unreadable?.length > 0 && (
                          <p>Presenti ma non decifrabili: {verifyResult.unreadable.join(", ")}.</p>
                        )}
                        <p className="mt-1">
                          Una nuova esecuzione della fase 2 ricopia i file assenti. Un oggetto
                          presente e non decifrabile indica una scrittura interrotta: va rimosso
                          dalla cartella di destinazione dal pannello Cloudflare, dopodiché la copia
                          lo riscrive.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </Step>

          <Step n={4} title="Attivazione della copia nuova" state={stepState("switched")}>
            <p className="mb-3">
              Una singola scrittura aggiorna insieme la cartella in lettura e la chiave in uso.
              Prima di procedere i file vengono contati nuovamente: se un file risulta assente dalla
              copia, l'attivazione viene rifiutata. Il controllo riguarda i file caricati mentre la
              copia era in corso.
            </p>
            {step === "ready_to_switch" && (
              <>
                <Button
                  onClick={() => setConfirm("switch")}
                  disabled={busy === "switch" || !verifyResult?.passed}
                  className="text-white"
                  style={{ backgroundColor: ACCENT }}
                >
                  {busy === "switch" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Attiva la copia nuova"}
                </Button>
                {!verifyResult?.passed && (
                  <p className="mt-2 text-xs text-slate-400">L'attivazione richiede una verifica superata.</p>
                )}
              </>
            )}
          </Step>

          {step === "switched" && (
            <>
              <Panel tone="good">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="min-w-0 text-sm text-emerald-900 leading-relaxed">
                    <h2 className="font-bold">Dopo l'attivazione</h2>
                    <ul className="mt-2 space-y-2">
                      <li>
                        <strong>Download.</strong> Un file allegato a un progetto deve scaricarsi e
                        aprirsi integro. Un file troncato o illeggibile indica una copia incompleta.
                      </li>
                      <li>
                        <strong>Anteprima.</strong> L'anteprima di un PDF verifica il percorso di
                        decifratura in memoria, distinto da quello del download.
                      </li>
                      <li>
                        <strong>Caricamento.</strong> Un file caricato dopo l'attivazione deve
                        scaricarsi correttamente.
                      </li>
                      <li>
                        <strong>Bucket.</strong> Nel bucket <code className="px-1 bg-white/60 rounded">{status?.bucket}</code>{" "}
                        sono presenti due copie, <code className="px-1 bg-white/60 rounded">{status?.previous?.prefix || "(radice)"}</code>{" "}
                        e <code className="px-1 bg-white/60 rounded">{status?.active?.prefix}</code>, con lo stesso numero di file.
                      </li>
                    </ul>
                    <p className="mt-3">
                      In caso di esito negativo, il ritorno alla copia precedente ripristina lo stato
                      antecedente all'attivazione.
                    </p>
                    <Button variant="outline" onClick={() => setConfirm("rollback")} disabled={busy === "rollback"} className="mt-3 border-emerald-300 bg-white">
                      <Undo2 className="w-4 h-4 mr-1.5" />Torna alla copia precedente
                    </Button>
                  </div>
                </div>
              </Panel>

              <Step n={5} title="Eliminazione della copia precedente" state="active">
                <p className="mb-3">
                  L'operazione rimuove dal bucket gli oggetti cifrati con la chiave precedente ed è
                  irreversibile. Finché quegli oggetti esistono il ritorno alla copia precedente
                  resta possibile. È opportuno eseguirla dopo un periodo di utilizzo ordinario della
                  copia nuova.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setConfirm("discard")}
                  disabled={busy === "discard"}
                  className="border-red-200 text-red-700 hover:bg-red-50"
                >
                  {busy === "discard" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1.5" />Elimina la copia precedente</>}
                </Button>
              </Step>
            </>
          )}

          {step === "idle" && (
            <p className="text-xs text-slate-400 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Nessuna rotazione in corso.
            </p>
          )}
        </div>
      </main>

      <AlertDialog open={!!confirm} onOpenChange={(v) => { if (!v) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "switch" && "Attivare la copia nuova?"}
              {confirm === "rollback" && "Tornare alla copia precedente?"}
              {confirm === "discard" && "Eliminare definitivamente la copia precedente?"}
              {confirm === "cancel" && "Annullare la rotazione?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "switch" && "La lettura dei file passa alla copia nuova. L'operazione è reversibile: gli oggetti precedenti restano nel bucket."}
              {confirm === "rollback" && "La lettura dei file torna alla copia precedente con la chiave precedente. La copia nuova resta nel bucket."}
              {confirm === "discard" && "Gli oggetti cifrati con la chiave precedente vengono rimossi dal bucket. L'operazione non è reversibile."}
              {confirm === "cancel" && "Gli oggetti già scritti nella cartella di destinazione vengono rimossi. Gli oggetti in uso non vengono modificati."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (confirm === "switch") onSwitch();
                if (confirm === "rollback") onRollback();
                if (confirm === "discard") onDiscard();
                if (confirm === "cancel") onCancel();
              }}
              className={confirm === "discard" ? "bg-red-600 hover:bg-red-700 text-white" : "text-white"}
              style={confirm === "discard" ? undefined : { backgroundColor: ACCENT }}
            >
              Conferma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
